import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
  type RefObject,
} from "react";
import { createPortal } from "react-dom";

const MENU_GAP_PX = 6;
const VIEWPORT_PAD_PX = 8;
const PORTAL_ROOT_ID = "ccs-floating-overlays";

function getPortalRoot(): HTMLElement {
  let root = document.getElementById(PORTAL_ROOT_ID);
  if (!root) {
    root = document.createElement("div");
    root.id = PORTAL_ROOT_ID;
    document.body.appendChild(root);
  }
  return root;
}

/** Visible region for the anchor, accounting for overflow:hidden/auto ancestors. */
function clipBoundsForAnchor(anchor: HTMLElement) {
  let top = VIEWPORT_PAD_PX;
  let bottom = window.innerHeight - VIEWPORT_PAD_PX;
  let left = VIEWPORT_PAD_PX;
  let right = window.innerWidth - VIEWPORT_PAD_PX;

  let el: HTMLElement | null = anchor.parentElement;
  while (el && el !== document.body) {
    const style = getComputedStyle(el);
    const clipsY =
      style.overflowY === "auto" ||
      style.overflowY === "scroll" ||
      style.overflowY === "hidden" ||
      style.overflow === "hidden";
    const clipsX =
      style.overflowX === "auto" ||
      style.overflowX === "scroll" ||
      style.overflowX === "hidden" ||
      style.overflow === "hidden";
    if (clipsY || clipsX) {
      const rect = el.getBoundingClientRect();
      if (clipsY) {
        top = Math.max(top, rect.top);
        bottom = Math.min(bottom, rect.bottom);
      }
      if (clipsX) {
        left = Math.max(left, rect.left);
        right = Math.min(right, rect.right);
      }
    }
    el = el.parentElement;
  }

  return { top, bottom, left, right };
}

function placeMenu(anchor: HTMLElement, menu: HTMLElement): CSSProperties {
  const rect = anchor.getBoundingClientRect();
  const menuWidth = menu.offsetWidth || 224;
  const menuHeight = menu.offsetHeight || 120;
  const bounds = clipBoundsForAnchor(anchor);

  const spaceBelow = bounds.bottom - rect.bottom - MENU_GAP_PX;
  const spaceAbove = rect.top - bounds.top - MENU_GAP_PX;
  const openUp = spaceBelow < menuHeight && spaceAbove > spaceBelow;

  let top = openUp ? rect.top - MENU_GAP_PX - menuHeight : rect.bottom + MENU_GAP_PX;
  top = Math.max(bounds.top, Math.min(top, bounds.bottom - menuHeight));

  let left = rect.right - menuWidth;
  left = Math.max(bounds.left, Math.min(left, bounds.right - menuWidth));

  return {
    position: "fixed",
    top,
    left,
    right: "auto",
    bottom: "auto",
    zIndex: 10000,
    visibility: "visible",
  };
}

export interface FloatingActionsMenuProps {
  readonly anchorRef: RefObject<HTMLElement | null>;
  readonly open: boolean;
  readonly onClose: () => void;
  readonly className?: string;
  readonly children: ReactNode;
}

/** Actions menu rendered in a portal so it is not clipped by overflow:auto panels. */
export function FloatingActionsMenu({
  anchorRef,
  open,
  onClose,
  className,
  children,
}: FloatingActionsMenuProps) {
  const menuRef = useRef<HTMLDivElement>(null);
  const [style, setStyle] = useState<CSSProperties>({
    position: "fixed",
    visibility: "hidden",
    zIndex: 10000,
  });

  useLayoutEffect(() => {
    if (!open || !anchorRef.current) return;

    const update = () => {
      const anchor = anchorRef.current;
      const menu = menuRef.current;
      if (!anchor || !menu) return;
      setStyle(placeMenu(anchor, menu));
    };

    update();
    const raf = requestAnimationFrame(() => update());
    const observer =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(() => update())
        : null;
    if (menuRef.current && observer) observer.observe(menuRef.current);

    window.addEventListener("resize", update);
    window.addEventListener("scroll", update, true);
    return () => {
      cancelAnimationFrame(raf);
      observer?.disconnect();
      window.removeEventListener("resize", update);
      window.removeEventListener("scroll", update, true);
    };
  }, [open, anchorRef, children]);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (ev: MouseEvent) => {
      const target = ev.target as Node;
      if (anchorRef.current?.contains(target)) return;
      if (menuRef.current?.contains(target)) return;
      onClose();
    };
    const onEscape = (ev: KeyboardEvent) => {
      if (ev.key === "Escape") onClose();
    };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onEscape);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onEscape);
    };
  }, [open, onClose, anchorRef]);

  if (!open) return null;

  return createPortal(
    <div
      ref={menuRef}
      className={`teacher-actions-menu teacher-actions-menu--floating${className ? ` ${className}` : ""}`}
      style={style}
      role="menu"
    >
      {children}
    </div>,
    getPortalRoot()
  );
}
